// src/components/patient/notifications/NotificationBell.jsx
//
// Header bell (Patient Appointments Task 4): unread count, recent list,
// mark one / mark all read, loading/error/empty/populated states, polling and
// focus refresh (useNotifications). Navigation only to allowlisted internal
// routes; anything else is shown but not clickable as a link.

import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { readApiError } from "../../../lib/apiErrors";
import { notificationRoute } from "../../../lib/notificationLinks";

import useNotifications from "./useNotifications";

const TONE_DOT = {
  success: "bg-emerald-500",
  warn: "bg-amber-500",
  danger: "bg-rose-500",
  violet: "bg-violet-500",
};

export function timeAgo(value, now = Date.now()) {
  if (!value) return "";
  const mins = Math.floor((now - new Date(value).getTime()) / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Intl.DateTimeFormat("en-CA", { day: "2-digit", month: "short" }).format(new Date(value));
}

export function NotificationList({ data, error, loading, onOpen, onRetry, compact = true }) {
  const items = data?.items || [];
  if (loading && !data) {
    return <p className="p-6 text-center text-sm font-semibold text-slate-500" role="status">Loading notifications…</p>;
  }
  if (error && !data) {
    return (
      <div role="alert" className="flex items-center justify-between gap-2 p-4 text-sm text-rose-700">
        <span>{readApiError(error, "Notifications could not be loaded.")}</span>
        <button type="button" onClick={onRetry} className="rounded-xl border border-rose-300 bg-white px-3 py-1 text-xs font-semibold">Retry</button>
      </div>
    );
  }
  if (!items.length) {
    return (
      <div className="p-8 text-center" data-testid="notifications-empty">
        <p className="text-sm font-black text-slate-950">No notifications</p>
        <p className="mt-1 text-xs text-slate-500">Appointment, invoice and ticket updates will appear here.</p>
      </div>
    );
  }
  return (
    <ul className={compact ? "max-h-[60vh] overflow-y-auto" : ""} aria-label="Notifications">
      {items.map((n) => {
        const route = notificationRoute(n);
        return (
          <li key={n.id} className={`border-b border-slate-100 ${n.is_unread ? "bg-sky-50/40" : ""}`}>
            <button
              type="button"
              onClick={() => onOpen(n, route)}
              className="w-full px-4 py-3 text-left transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-sky-300"
              aria-label={`${n.title}${n.is_unread ? ", unread" : ""}`}
              data-testid="notification-item"
            >
              <div className="flex items-start gap-3">
                <span aria-hidden="true" className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${TONE_DOT[n.tone] || "bg-sky-500"}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <span className="truncate text-sm font-black text-slate-950">{n.title}</span>
                    <span className="shrink-0 text-[11px] font-semibold text-slate-400">{timeAgo(n.created_at)}</span>
                  </div>
                  {n.body ? <p className="mt-1 line-clamp-2 text-xs leading-5 text-slate-600">{n.body}</p> : null}
                  <div className="mt-1 flex items-center gap-2 text-[11px] font-semibold">
                    {route ? <span className="text-sky-700">{n.action_label || "Open"}</span> : null}
                    {n.is_unread ? <span className="text-sky-700">Unread</span> : <span className="text-slate-400">Seen</span>}
                  </div>
                </div>
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export default function NotificationBell({ open, onOpenChange }) {
  const navigate = useNavigate();
  const { data, error, loading, refresh, markRead, markAllRead } = useNotifications({ limit: 8 });
  const [markAllError, setMarkAllError] = useState("");
  const [markingAll, setMarkingAll] = useState(false);
  const ref = useRef(null);
  const unread = Number(data?.unread_count || 0);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => {
      if (ref.current && !ref.current.contains(event.target)) onOpenChange(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") onOpenChange(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onOpenChange]);

  async function openItem(notification, route) {
    await markRead(notification);
    if (route) {
      onOpenChange(false);
      navigate(route);
    }
  }

  async function onMarkAll() {
    if (markingAll) return;
    setMarkingAll(true);
    setMarkAllError("");
    try {
      await markAllRead();
    } catch (err) {
      setMarkAllError(readApiError(err, "Couldn't mark notifications as read."));
    } finally {
      setMarkingAll(false);
    }
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
        className={`relative flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:bg-slate-50 ${open ? "ring-4 ring-primary/15" : ""}`}
        data-testid="notification-bell"
      >
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M18 8a6 6 0 1 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
          <path d="M10 21a2 2 0 0 0 4 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        {unread > 0 ? (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-black text-white ring-2 ring-white" data-testid="notification-count">
            {unread > 9 ? "9+" : unread}
          </span>
        ) : null}
      </button>
      <span className="sr-only" aria-live="polite">{unread > 0 ? `${unread} unread notifications` : ""}</span>

      {open ? (
        <div role="dialog" aria-label="Notifications" className="absolute right-0 z-50 mt-3 w-[min(380px,calc(100vw-2rem))] overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl">
          <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-4">
            <div>
              <p className="text-base font-black text-slate-950">Notifications</p>
              <p className="mt-1 text-xs text-slate-500">{unread > 0 ? `${unread} unread` : "You are all caught up"}</p>
            </div>
            <button type="button" onClick={onMarkAll} disabled={unread === 0 || markingAll} aria-busy={markingAll || undefined}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-700 disabled:cursor-not-allowed disabled:opacity-50">
              Mark all read
            </button>
          </div>
          {markAllError ? <p role="alert" className="px-4 py-2 text-xs text-rose-700">{markAllError}</p> : null}
          <NotificationList data={data} error={error} loading={loading} onOpen={openItem} onRetry={refresh} />
          <div className="flex items-center justify-between px-4 py-3">
            <Link to="/patient/notifications" onClick={() => onOpenChange(false)} className="text-sm font-black text-primary hover:underline">View all</Link>
            <Link to="/patient/appointments" onClick={() => onOpenChange(false)} className="text-sm font-black text-primary hover:underline">Appointments</Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}
